/**
 * Tutorial API Service
 * Handles API calls for tutorial progress tracking
 */
import axios from 'axios';
import { HOST_URL, API_V3 } from '@/config/api';

/**
 * Tutorial task title (must match backend exactly)
 */
const TUTORIAL_TASK_TITLE = '判官頁面流程操作';

/**
 * Tutorial API Service
 */
export const TutorialApiService = {
  /**
   * Get tutorial progress
   * @param {Object} params - Parameters
   * @param {string} params.lineId - Player's LINE ID
   * @param {string} params.groupId - Group ID
   * @returns {Promise<Object>} Tutorial progress with progress_detail
   */
  async getProgress({ lineId, groupId }) {
    try {
      const response = await axios.get(
        `${HOST_URL}${API_V3}/tutorial_tasks/${encodeURIComponent(TUTORIAL_TASK_TITLE)}`,
        {
          params: {
            line_id: lineId,
            group_id: groupId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('[TutorialApiService] Failed to get progress:', error.message);
      return { progress_detail: {} };
    }
  },

  /**
   * Update tutorial progress for a specific page
   * @param {Object} params - Parameters
   * @param {string} params.lineId - Player's LINE ID
   * @param {string} params.groupId - Group ID
   * @param {string} params.page - Page name (exhibits, hints, souls)
   * @returns {Promise<Object>} API response
   */
  async updateProgress({ lineId, groupId, page }) {
    try {
      const response = await axios.put(
        `${HOST_URL}${API_V3}/tutorial_tasks/${encodeURIComponent(TUTORIAL_TASK_TITLE)}`,
        null,
        {
          params: {
            line_id: lineId,
            group_token: groupId,
            page: page,
            progress: 'finished'
          }
        }
      );
      return response.data;
    } catch (error) {
      // Handle known error responses
      if (error.response && error.response.data && error.response.data.error) {
        const errorMsg = error.response.data.error;

        // "任務已完成囉～" is not really an error - tutorial was already done
        if (errorMsg.includes('任務已完成')) {
          return { success: true, alreadyCompleted: true };
        }

        console.warn('[TutorialApiService] API error:', errorMsg);
        return { success: false, error: errorMsg };
      }

      console.error('[TutorialApiService] Network error:', error.message);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if tutorial mode is active from API response
   * @param {Object} apiResponse - Response from page API (exhibits, hints, souls)
   * @returns {Object} Tutorial state
   */
  parseTutorialState(apiResponse) {
    const tutorialMode = apiResponse && apiResponse.story && apiResponse.story.tutorialMode === true;
    const task = apiResponse && apiResponse.member && apiResponse.member.task;
    const currentStep = task ? task.currentTutorialStep : null;
    const progressDetail = task ? task.progress_detail : {};

    return {
      isEnabled: tutorialMode,
      isCompleted: currentStep === 'finished',
      currentStep: currentStep,
      progressDetail: progressDetail || {},
      shouldStart: tutorialMode && currentStep !== 'finished'
    };
  }
};

export default TutorialApiService;
